// The "datatalk" chat panel (as opposed to LeitlinienChatPanel's RAG chat): streams LLM
// answers about a patient's ML prediction ("prediction" mode) or raw monitoring data
// ("monitoring" mode) via DataTalkChatStreamSpecification, and lets the professional open
// PatientMessageModal to draft a message to the patient from the same context.
import { useState } from "react"
import {
    AnalysisMessageEntry,
    AppendAnalysisMessageSpecification,
    ContextOption,
    DataTalkChatConversationMessage,
    DataTalkChatStreamSpecification,
    ValueType,
} from "pokal_api"
import ChatShell from "../shared/ChatShell"
import { useChatStream } from "../shared/useChatStream"
import ConversationSelector from "../shared/ConversationSelector"
import PatientMessageModal from "./PatientMessageModal"
import { useAppSelector } from "../../../../redux/store/hooks"
import { selectOpenAIKey } from "../../../../redux/AccountSettings"

type Props = {
    patientId: string
    mode: "monitoring" | "prediction"
    contextSelections?: Partial<Record<ValueType, ContextOption[]>>
}

export default function DataChatPanel({ patientId, mode, contextSelections }: Props) {
    const [isModalOpen, setIsModalOpen] = useState(false)

    const [conversationId, setConversationId] = useState<string | null>(null)
    const [initialMessages, setInitialMessages] = useState<{ role: "user" | "assistant"; content: string }[]>([])

    const openaiApiKey = useAppSelector(selectOpenAIKey)

    const chat = useChatStream({
        patientId,
        conversationId,
        initialMessages,
        startStream: async (message, history, model) => {
            const conversationHistory: DataTalkChatConversationMessage[] = history.map(m => ({
                role: m.role,
                content: m.content,
            }))
            // conversationId rides along on the request body so the backend can persist
            // the assistant's reply itself once generation finishes - this lets the answer
            // survive a page refresh mid-stream. Not part of the pokal_api request type
            // (avoids a shared-package publish); the backend reads it off req.body directly.
            const body = {
                patient: patientId,
                message,
                mode,
                conversation_history: conversationHistory,
                llm_model: model,
                context_selections: contextSelections,
                openai_api_key: openaiApiKey ?? undefined,
                conversationId,
            }
            return (await DataTalkChatStreamSpecification.request(body)) as unknown as ReadableStream
        },
        onUserMessage: async content => {
            if (!conversationId) return
            try {
                await AppendAnalysisMessageSpecification.request({
                    conversationId,
                    role: "user",
                    content,
                })
            } catch {}
        },
        // No onAssistantMessage: the backend now persists the assistant's reply via
        // StreamPokalRoute's onStreamComplete, regardless of whether this tab is still
        // connected. Persisting it again here would create a duplicate message.
    })

    const hasContext = contextSelections
        ? Object.values(contextSelections).some(opts => opts && opts.length > 0)
        : true

    const inputDisabled = mode === "monitoring" && !hasContext

    return (
        <>
            <ConversationSelector
                patientId={patientId}
                mode={mode}
                onConversationChange={(id, messages) => {
                    setInitialMessages(
                        messages.map((m: AnalysisMessageEntry) => ({ role: m.role, content: m.content }))
                    )
                    setConversationId(id)
                }}
                onOpenCompose={() => setIsModalOpen(true)}
            />
            <ChatShell
                messages={chat.messages}
                streaming={chat.streaming}
                loading={chat.loading}
                input={chat.input}
                onInputChange={chat.setInput}
                onSubmit={chat.handleSubmit}
                availableModels={chat.availableModels}
                selectedModel={chat.selectedModel}
                onModelChange={chat.setSelectedModel}
                emptyHint={
                    mode === "monitoring"
                        ? "Stellen Sie Fragen zu den Monitoring-Daten des Patienten."
                        : "Stellen Sie Fragen zu den Vorhersagen und ihren Einflussfaktoren."
                }
                placeholder={
                    inputDisabled ? "Bitte zuerst Kontext auswählen..." : "Frage zu Patientendaten stellen..."
                }
                inputDisabled={inputDisabled}
                submitDisabledTitle="Bitte zuerst Kontext auswählen"
            />
            <PatientMessageModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                patientId={patientId}
                mode={mode}
                selectedModel={chat.selectedModel}
                openaiApiKey={openaiApiKey ?? undefined}
            />
        </>
    )
}
